import { useCallback } from "react";
import { useSelector } from "react-redux";
import { Field } from "redux-form/immutable";

import {
  Box,
  Button,
  Column,
  Columns,
  Icon,
  Inline,
  Stack,
  TextButton,
  Tooltip,
  useBreakpoint
} from "@seneca/senekit";

import { regionCodes } from "common/static/regions";

import { getUserEmail } from "seneca-common/features/user/state";
import { validateEmailStrictly } from "seneca-common/utils/functions/validationFunctions";

import FormCheckbox from "features/common/forms/components/FormCheckbox";
import SchoolsDropdownMenu from "features/common/forms/components/SchoolsDropdownMenu";
import SubmitCounterPulseWrapper from "features/common/forms/components/SubmitCounterPulseWrapper";
import YearGroupDropdown from "features/common/forms/components/YearGroupDropdown";
import useAppName from "features/common/hooks/useAppName";
import { FormTextInput } from "features/forms/components/FormTextInput";
import { getCollectUserSchool, getRegion } from "features/regions/selectors";
import ErrorMessage from "features/sign-up/components/ErrorMessage";

import SignUpWithProgressBarWrapper from "../../../components/SignUpWithProgressBarWrapper";
import ConsentCheckboxLabel from "./ConsentCheckboxLabel";
import styles from "./SignUpFormStudentDetails.module.scss";

type Props = {
  handleSubmit: () => void;
  submitting: boolean;
  error: string;
  pulse: boolean;
  // injected by SubmitCounterPulseWrapper
  incrementSubmitCount: () => void;
  onBackClick: () => void;
  pageNumber: number;
  totalNumberOfPages: number;
  collectUserYearGroup: boolean;
};

function SignUpFormStudentDetails({
  handleSubmit,
  submitting,
  error,
  pulse,
  incrementSubmitCount,
  onBackClick,
  pageNumber,
  totalNumberOfPages,
  collectUserYearGroup
}: Props) {
  const includeSchool = useSelector(getCollectUserSchool);

  const ownEmail = useSelector(getUserEmail);
  const region = useSelector(getRegion);
  const isBR = region === regionCodes.BR;

  const validateParentEmail = useCallback(
    (parentEmail: string | null | undefined) => {
      if (!parentEmail && isBR) return "Please enter a guardian email.";

      if (!parentEmail) return undefined;

      if (parentEmail.toLowerCase() === ownEmail?.toLowerCase()) {
        return "Parent email must be different to the email you signed up with";
      }

      return validateEmailStrictly(parentEmail);
    },
    [ownEmail, isBR]
  );

  return (
    <SignUpWithProgressBarWrapper
      header="Complete your account"
      pageNumber={pageNumber}
      totalNumberOfPages={totalNumberOfPages}
    >
      <Box width="100%">
        <form onSubmit={handleSubmit}>
          <Box marginTop="large">
            <Stack space="xlarge">
              <Box
                display="grid"
                gridTemplateColumns={[
                  "100%",
                  "100%",
                  "minmax(0, 2fr) minmax(0, 1fr)"
                ]}
                gap="large"
              >
                {includeSchool && (
                  <Field name="school" component={SchoolsDropdownMenu} />
                )}
                {collectUserYearGroup && (
                  <Field
                    name="yearGroup"
                    component={YearGroupDropdown}
                    pulse={pulse}
                  />
                )}
              </Box>
              <Field
                pulse={pulse}
                name="guardianEmail"
                component={FormTextInput}
                type="email"
                placeholder="Your parent's email"
                label={<GuardianEmailInputLabel />}
                validate={validateParentEmail}
              />
              <Field
                name="consent"
                component={FormCheckbox}
                checkboxWrapperClassName={styles.checkboxWrapperClassName}
                label={<ConsentCheckboxLabel />}
                pulse={pulse}
              />
              <ErrorMessage>{error}</ErrorMessage>
            </Stack>
          </Box>

          <Columns
            collapseBelow="tablet"
            reverseOnCollapse
            alignY="baseline"
            alignX="center"
            space="xlarge"
          >
            <Column alignX={["center", "flex-start"]}>
              <TextButton size="medium" onClick={onBackClick}>
                Previous step
              </TextButton>
            </Column>
            <Column alignX={["center", "flex-end"]}>
              <Button
                onClick={incrementSubmitCount}
                loading={submitting}
                disabled={submitting}
              >
                Finish sign up
              </Button>
            </Column>
          </Columns>
        </form>
      </Box>
    </SignUpWithProgressBarWrapper>
  );
}

function GuardianEmailInputLabel() {
  const { isMobileOnly } = useBreakpoint();
  const appName = useAppName();

  return (
    <Inline justifyContent="space-between" alignItems="center">
      Parent email
      <Box onClick={preventDefault}>
        <Tooltip
          position="topLeft"
          showArrow
          contrast="high"
          maxWidth={isMobileOnly ? "300px" : undefined}
          tooltip={
            <Box fontWeight="normal" padding={["small", "xlarge"]}>
              Your parent's email will be used to confirm consent to use{" "}
              {appName} and allow them to sign up for a parent account to keep
              up to date with your learning.
            </Box>
          }
        >
          <Box>
            <Icon
              iconName="help"
              accessibilityLabel="What's this?"
              size="small"
            />
          </Box>
        </Tooltip>
      </Box>
    </Inline>
  );
}

function preventDefault(e: React.MouseEvent) {
  return e.preventDefault();
}

export default SubmitCounterPulseWrapper(SignUpFormStudentDetails);
